import React, { useCallback, useEffect, useRef, useState } from "react";
import { useFirebasePermissions } from "@thewebops/notifications";
import {
  CARDS,
  currentDevice,
  currentInstallCardKind,
  deviceKind,
  dismissForAWeek,
  getInstallPrompt,
  iosBrowser,
  isIPad,
  spendInstallPrompt,
  subscribeInstallPrompt,
} from "utils/installApp";
import { pushWorkerReady } from "utils/pushWorker";
import IOSInstallGuide from "./IOSInstallGuide";
import { BellIcon, BellOffIcon, CompassIcon, DownloadIcon, GlobeIcon } from "./icons";
import {
  Card,
  Top,
  Logo,
  IconBox,
  Texts,
  Title,
  Body,
  Panel,
  Buttons,
  PrimaryButton,
  SecondaryButton,
  Note,
} from "./styled";

const LOGO = `${process.env.PUBLIC_URL}/logo192.png`;
const TITLE_ID = "install-card-title";
const COPIED_MS = 2000;
// The link the in-app browser cards copy: the conversations list.
const CHAT_PATH = "/app/chat";

// "Got it" on the blocked card hides it until the next page load, also when the chat is opened again.
let hiddenThisVisit = false;

const kindNow = () => (hiddenThisVisit ? null : currentInstallCardKind());

// Copies with the clipboard API, or the older copy command where that isn't allowed.
const copyText = (text) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text).catch(() => copyWithCommand(text));
  }
  return Promise.resolve().then(() => copyWithCommand(text));
};

const copyWithCommand = (text) => {
  const field = document.createElement("textarea");
  field.value = text;
  field.setAttribute("readonly", "");
  field.style.position = "fixed";
  field.style.opacity = "0";
  document.body.appendChild(field);
  field.select();
  // iPhone Safari selects nothing with select() alone.
  field.setSelectionRange(0, text.length);
  try {
    if (!document.execCommand("copy")) throw new Error("copy refused");
  } finally {
    document.body.removeChild(field);
  }
};

const Header = ({ icon, title, children }) => (
  <Top>
    {icon}
    <Texts>
      <Title id={TITLE_ID}>{title}</Title>
      <Body>{children}</Body>
    </Texts>
  </Top>
);

const BlockedSteps = ({ device }) => {
  const kind = deviceKind(device);
  // iPhones only have notifications in the Home Screen app, so only it can show them as blocked.
  if (kind === "ios") {
    return (
      <Panel>
        <ol>
          <li>
            Open your {isIPad(device) ? "iPad" : "iPhone"}'s <b>Settings</b>
          </li>
          <li>
            Tap <b>Notifications</b>, then <b>Nutivate Coach</b>
          </li>
          <li>
            Switch on <b>Allow Notifications</b>
          </li>
        </ol>
      </Panel>
    );
  }
  if (kind === "android") {
    return (
      <Panel>
        <p>
          In Chrome: tap the icon left of the web address, then <b>Permissions → Notifications → Allow</b>.
        </p>
        <p>
          In the installed app: open the phone's <b>Settings → Apps → Nutivate Coach → Notifications</b> → on.
        </p>
      </Panel>
    );
  }
  return (
    <Panel>
      <p>
        Click the lock or site-information icon left of the web address, then <b>Notifications → Allow</b>.
      </p>
    </Panel>
  );
};

// The install / notifications card at the top of the conversations list. Which card shows is decided in
// utils/installApp; this renders it and handles its buttons.
const InstallAppCard = () => {
  const { setFirebaseToken } = useFirebasePermissions();
  const [kind, setKind] = useState(kindNow);
  const [busy, setBusy] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [copy, setCopy] = useState("idle");
  const installButton = useRef(null);
  const mounted = useRef(true);
  const copiedTimer = useRef(null);

  const refresh = useCallback(() => {
    if (mounted.current) setKind(kindNow());
  }, []);

  useEffect(() => {
    // Android's install prompt can arrive after the card shows; notifications can be switched on in Settings
    // while the app is in the background.
    const unsubscribe = subscribeInstallPrompt(refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      mounted.current = false;
      clearTimeout(copiedTimer.current);
      unsubscribe();
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [refresh]);

  const notNow = () => {
    dismissForAWeek();
    setKind(null);
  };

  const gotIt = () => {
    hiddenThisVisit = true;
    setKind(null);
  };

  // iPhones only let a website ask for permission from a tap, so the token is registered here, the same way the
  // admin panel does at sign-in, once the coach has allowed notifications.
  const turnOn = () => {
    setBusy(true);
    new Promise((resolve) => resolve(Notification.requestPermission()))
      .then((permission) => {
        if (permission !== "granted") return undefined;
        return pushWorkerReady()
          .catch(() => {})
          .then(() => setFirebaseToken());
      })
      // setFirebaseToken reports its own token errors (and retries on the next load once allowed), so this is only
      // a refused permission request.
      .catch((error) => console.warn("Notifications could not be turned on", error))
      .then(() => {
        if (mounted.current) setBusy(false);
        refresh();
      });
  };

  // Android's own install dialog. A prompt is used up once shown, so the card then offers notifications.
  const install = () => {
    const prompt = getInstallPrompt();
    if (!prompt) return refresh();
    setBusy(true);
    return new Promise((resolve) => {
      prompt.prompt();
      resolve(prompt.userChoice);
    })
      .catch(() => {})
      .then(() => {
        if (mounted.current) setBusy(false);
        spendInstallPrompt();
      });
  };

  const closeGuide = useCallback(() => {
    setGuideOpen(false);
    if (installButton.current) installButton.current.focus();
  }, []);

  const copyLink = () => {
    copyText(`${window.location.origin}${CHAT_PATH}`)
      .then(() => "copied")
      .catch(() => "failed")
      .then((result) => {
        if (!mounted.current) return;
        setCopy(result);
        clearTimeout(copiedTimer.current);
        if (result === "copied") copiedTimer.current = setTimeout(() => mounted.current && setCopy("idle"), COPIED_MS);
      });
  };

  if (!kind) return null;
  const device = currentDevice();
  const notNowButton = (
    <SecondaryButton type="button" onClick={notNow}>
      Not now
    </SecondaryButton>
  );

  if (kind === CARDS.installIOS || kind === CARDS.installAndroid) {
    const ios = kind === CARDS.installIOS;
    return (
      <Card aria-labelledby={TITLE_ID}>
        <Header icon={<Logo src={LOGO} alt="" />} title="Get the Nutivate Coach app">
          Get a notification as soon as a client messages you.
        </Header>
        <Buttons>
          <PrimaryButton
            ref={installButton}
            type="button"
            disabled={busy}
            aria-haspopup={ios ? "dialog" : undefined}
            onClick={ios ? () => setGuideOpen(true) : install}
          >
            <DownloadIcon />
            Install app
          </PrimaryButton>
          {notNowButton}
        </Buttons>
        {ios && guideOpen && <IOSInstallGuide browser={iosBrowser(device.userAgent)} onClose={closeGuide} />}
      </Card>
    );
  }

  if (kind === CARDS.turnOn) {
    return (
      <Card aria-labelledby={TITLE_ID}>
        <Header
          icon={
            <IconBox>
              <BellIcon />
            </IconBox>
          }
          title="Turn on notifications"
        >
          {device.standalone
            ? "Last step: so you know the moment a client messages you."
            : "Get a notification as soon as a client messages you."}
        </Header>
        <Buttons>
          <PrimaryButton type="button" disabled={busy} onClick={turnOn}>
            Turn on
          </PrimaryButton>
          {notNowButton}
        </Buttons>
      </Card>
    );
  }

  if (kind === CARDS.blocked) {
    return (
      <Card aria-labelledby={TITLE_ID}>
        <Header
          icon={
            <IconBox>
              <BellOffIcon />
            </IconBox>
          }
          title="Notifications are off"
        >
          Turn them on to know when clients message you.
        </Header>
        <BlockedSteps device={device} />
        <Buttons>
          <PrimaryButton type="button" onClick={gotIt}>
            Got it
          </PrimaryButton>
          {notNowButton}
        </Buttons>
      </Card>
    );
  }

  // In-app browsers (Instagram, Facebook, TikTok…) can't install the app or get pushes.
  const safari = kind === CARDS.openInSafari;
  const browserName = safari ? "Safari" : "Chrome";
  const copyNotes = {
    idle: `Or paste the copied link into ${browserName}.`,
    copied: `Link copied. Paste it into ${browserName}.`,
    failed: `Couldn't copy the link. Copy the web address from the top of the screen instead.`,
  };
  return (
    <Card aria-labelledby={TITLE_ID}>
      <Header
        icon={<IconBox color={safari ? "#0a7aff" : "#1a73e8"}>{safari ? <CompassIcon /> : <GlobeIcon />}</IconBox>}
        title={`Open Nutivate in ${browserName}`}
      >
        This app's browser can't install Nutivate, so you won't get notifications here.
      </Header>
      <Panel>
        <p>
          <b>How:</b> Tap {safari ? "⋯" : "⋮"} at the top right, then{" "}
          {safari ? <b>“Open in external browser”</b> : <b>“Open in Chrome”</b>} or{" "}
          {safari ? <b>“Open in Safari”</b> : <b>“Open in browser”</b>}.
        </p>
      </Panel>
      <Buttons>
        <PrimaryButton type="button" onClick={copyLink}>
          {copy === "copied" ? "Copied" : "Copy link"}
        </PrimaryButton>
        {notNowButton}
      </Buttons>
      <Note aria-live="polite">{copyNotes[copy]}</Note>
    </Card>
  );
};

export default InstallAppCard;
